import { useEffect, useRef, useState } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import meetingsVoiceCopy from '@/i18n/ns/meetings-voice';
import voiceSignatureCopy from '@/i18n/ns/voice-signature';
import meetingsSpeakersCopy from '@/i18n/ns/meetings-speakers';
import { voiceApi, type VoiceMe } from '@/company/voice-api';
import { signatureError } from '@/voice/signature-error';
import { workspaceApi } from './api';
import { meetingsApi } from './meetings-api';
import { useWorkspace } from './context';
import { enrollmentChoices, enrollmentSamples } from './meeting-enrolment-model';
import type { CompanyMeeting, MeetingSpeaker } from './types';

const hasCurrentSignature = (value: VoiceMe) =>
  value.voiceprint?.status === 'active' && value.consent?.version === value.currentConsentVersion;

/** One prompt per person. Only the consent button starts enrollment. */
export function MeetingVoiceEnrolment({
  meeting,
  onChanged,
}: {
  meeting: CompanyMeeting;
  onChanged?: () => void;
}) {
  const copy = useNamespaceCopy(meetingsVoiceCopy);
  const signature = useNamespaceCopy(voiceSignatureCopy);
  const speakersCopy = useNamespaceCopy(meetingsSpeakersCopy);
  const name = (voice: MeetingSpeaker) =>
    voice.name ?? formatCopy(speakersCopy.speakerN, { n: voice.speakerNumber });
  const locale = useAppCatalogLocale();
  const userId = useWorkspace().user?.id;
  const choices = enrollmentChoices(meeting, userId);
  const choicesKey = choices.map((v) => `${v.label}:${v.participantId ?? ''}`).join('|');
  const [me, setMe] = useState<VoiceMe | null>(null);
  const [selected, setSelected] = useState('');
  const [step, setStep] = useState<'closed' | 'consent' | 'processing' | 'saved'>('closed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const audio = useRef<HTMLAudioElement | null>(null);
  const endMs = useRef<number | null>(null);
  const generation = useRef(0);
  const operation = useRef(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const voice = choices.find((v) => v.label === selected) ?? choices[0];
  const ranges = voice ? enrollmentSamples(meeting, voice) : [];

  useEffect(() => {
    const version = ++generation.current;
    operation.current = false;
    setMe(null);
    setStep('closed');
    setError(null);
    setBusy(false);
    setUrl('');
    setSelected(choices[0]?.label ?? '');
    if (choices.length)
      void voiceApi
        .me()
        .then(async (value) => {
          if (generation.current !== version) return;
          setMe(value);
          if (
            value.available &&
            value.org.signaturesEnabled &&
            !hasCurrentSignature(value) &&
            !value.promptDismissed
          ) {
            setStep('consent');
            // The durable once-per-person marker records display, not biometric consent.
            await voiceApi.dismissPrompt();
          }
        })
        .catch((failure) => {
          if (generation.current === version) setError(signatureError(failure, signature));
        });
    return () => {
      generation.current++;
      clearTimeout(pollTimer.current);
      audio.current?.pause();
    };
  }, [userId, meeting.id, choicesKey]);

  async function dismiss() {
    if (operation.current) return;
    const version = generation.current;
    audio.current?.pause();
    setStep('closed');
    try {
      await voiceApi.dismissPrompt();
    } catch (failure) {
      if (generation.current === version) setError(signatureError(failure, signature));
    }
  }
  async function poll(id: string, version: number, deadline: number) {
    try {
      const result = await voiceApi.enrolmentStatus(id);
      if (generation.current !== version) return;
      if (result.status === 'done') {
        setStep('saved');
        setBusy(false);
        operation.current = false;
        onChanged?.();
        return;
      }
      if (result.status === 'failed') throw new Error(result.failureCode);
      if (Date.now() >= deadline) throw new Error('voice_enrolment_expired');
      pollTimer.current = setTimeout(() => void poll(id, version, deadline), 1000);
    } catch (failure) {
      if (generation.current === version) {
        setError(signatureError(failure, signature));
        setStep('consent');
        setBusy(false);
        operation.current = false;
      }
    }
  }
  async function enrol() {
    if (!voice || !userId || !me || operation.current) return;
    operation.current = true;
    const version = generation.current;
    setBusy(true);
    setError(null);
    audio.current?.pause();
    try {
      const fresh = await voiceApi.me();
      if (generation.current !== version) return;
      if (
        !fresh.available ||
        !fresh.org.signaturesEnabled ||
        hasCurrentSignature(fresh) ||
        fresh.currentConsentVersion !== me.currentConsentVersion
      )
        throw new Error('voice_consent_version_mismatch');
      if (fresh.consent?.version !== me.currentConsentVersion)
        await voiceApi.consent({ version: me.currentConsentVersion, method: 'prompt', locale });
      if (generation.current !== version) return;
      let participantId = meeting.participants?.find((p) => p.userId === userId)?.id;
      if (!participantId)
        participantId = (await meetingsApi.addParticipant(meeting.id, { kind: 'member', userId }))
          .id;
      if (generation.current !== version) return;
      // This tap confirms the selected speaker is the signed-in person.
      await meetingsApi.updateSpeaker(meeting.id, voice.label, { participantId });
      if (generation.current !== version) return;
      const result = await voiceApi.enrolFromMeeting(meeting.id, voice.label);
      if (generation.current !== version) return;
      setStep('processing');
      void poll(result.enrolmentId, version, Date.now() + 120_000);
    } catch (failure) {
      if (generation.current === version) {
        setError(signatureError(failure, signature));
        setBusy(false);
        operation.current = false;
      }
    }
  }
  async function play(index: number) {
    const version = generation.current;
    const range = ranges[index];
    if (!range) return;
    try {
      const target = url || (await workspaceApi.fileDownloadTarget(meeting.fileId!, true)).url;
      if (generation.current !== version || !audio.current || !target) return;
      setUrl(target);
      audio.current.src = target;
      endMs.current = range.endMs;
      audio.current.currentTime = range.startMs / 1000;
      await audio.current.play();
    } catch (failure) {
      if (generation.current === version) setError(signatureError(failure, signature));
    }
  }
  if (!voice || !me?.available || !me.org.signaturesEnabled || hasCurrentSignature(me)) return null;
  return (
    <section className="ws-voice-enrolment" aria-label={copy.useMyVoice}>
      {step === 'closed' && (
        <button className="ws-button" onClick={() => setStep('consent')}>
          {copy.useMyVoice}
        </button>
      )}
      {step === 'consent' && (
        <div role="group" aria-label={copy.useMyVoice}>
          {choices.length > 1 && (
            <label>
              {speakersCopy.speakersTitle}
              <select
                disabled={busy}
                value={voice.label}
                onChange={(event) => {
                  audio.current?.pause();
                  setSelected(event.target.value);
                }}
              >
                {choices.map((v: MeetingSpeaker) => (
                  <option key={v.label} value={v.label}>
                    {name(v)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <p>{formatCopy(copy.isSpeakerYou, { speaker: name(voice) })}</p>
          <p>{signature.consentBody}</p>
          <audio
            ref={audio}
            preload="none"
            onTimeUpdate={() => {
              if (
                audio.current &&
                endMs.current !== null &&
                audio.current.currentTime * 1000 >= endMs.current
              )
                audio.current.pause();
            }}
          />
          <div className="ws-voice-review-actions">
            {ranges.map((range, index) => (
              <button
                key={range.index}
                className="ws-button"
                disabled={busy}
                onClick={() => void play(index)}
              >
                ▶ {formatCopy(copy.progress, { n: index + 1, total: ranges.length })}
              </button>
            ))}
          </div>
          <button className="ws-button" disabled={busy} onClick={() => void enrol()}>
            {copy.saveMyVoice}
          </button>
          <button className="ws-button" disabled={busy} onClick={() => void dismiss()}>
            {copy.promptNoThanks}
          </button>
        </div>
      )}
      {step === 'processing' && <p role="status">{signature.processing}</p>}
      {step === 'saved' && <p role="status">{signature.saved}</p>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
